import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/v2/lib/files";
import SmartVideo, { getYouTubeId } from "@/v2/components/SmartVideo";
import { VIDEO_STATUS_AR } from "@/v2/lib/rbac";
import { GlassCard, Chip, Skeleton, Modal, Confirm, PageHead, UploadBtn } from "@/v2/admin/ui";
import {
  Clapperboard, Pencil, Power, PlayCircle, PauseCircle, Trash2, Eye,
  Plus, X, ArrowUp, ArrowDown, ListVideo,
} from "lucide-react";

const V_TONE = { active: "emerald", disabled: "red", locked: "gold", scheduled: "gold" };

const cumulativeStarts = (items) => {
  let elapsed = 0;
  return items.map((item) => {
    const start = elapsed;
    elapsed += Number(item.duration_seconds || 0);
    return start;
  });
};

const rebaseChapters = (chapters, oldItems, newItems, mapPart) => {
  const oldStarts = cumulativeStarts(oldItems);
  const newStarts = cumulativeStarts(newItems);
  return chapters.flatMap((chapter) => {
    const oldPart = Number(chapter.part_index || 0);
    const newPart = mapPart(oldPart);
    if (newPart === null || newPart < 0 || newPart >= newItems.length) return [];
    const localOffset = Math.max(0, Number(chapter.start_seconds || 0) - Number(oldStarts[oldPart] || 0));
    return [{ ...chapter, part_index: newPart, start_seconds: Math.round(newStarts[newPart] + localOffset) }];
  }).sort((a, b) => Number(a.start_seconds || 0) - Number(b.start_seconds || 0));
};

function VideoForm({ ch, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    video_url: ch.video_url || ch.video || "", video_title: ch.video_title || "",
    video_description: ch.video_description || "", video_duration: ch.video_duration || "",
    thumbnail: ch.thumbnail || "", video_required_pct: ch.video_required_pct ?? 80,
    video_status: ch.video_status || "active",
    video_playlist: Array.isArray(ch.video_playlist) ? ch.video_playlist.map((item) => ({ ...item })) : [],
    video_chapters: Array.isArray(ch.video_chapters) ? ch.video_chapters.map((item) => ({ ...item })) : [],
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const set = (k) => (e) => {
    const value = e.target.value;
    setF((current) => ({ ...current, [k]: value }));
  };
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["adm-challenges"] }); qc.invalidateQueries({ queryKey: ["challenges"] }); };

  const setSegment = (index, key, value) => setF((current) => {
    const list = current.video_playlist.map((item, i) => i === index ? { ...item, [key]: value } : item);
    return {
      ...current,
      video_playlist: list,
      video_chapters: key === "duration_seconds"
        ? rebaseChapters(current.video_chapters, current.video_playlist, list, (part) => part)
        : current.video_chapters,
    };
  });
  const addSegment = () => setF((current) => {
    const index = current.video_playlist.length;
    const start = current.video_playlist.reduce((sum, item) => sum + Number(item.duration_seconds || 0), 0);
    return {
      ...current,
      video_playlist: [...current.video_playlist, {
        title: `المقطع ${index + 1}`, url: "", duration_seconds: 0, part_index: index,
      }],
      video_chapters: [...current.video_chapters, {
        title: `المقطع ${index + 1}`, start_seconds: Math.round(start), part_index: index,
      }],
    };
  });
  const removeSegment = (index) => setF((current) => {
    const list = current.video_playlist
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, part_index: i }));
    return {
      ...current,
      video_playlist: list,
      video_chapters: rebaseChapters(
        current.video_chapters, current.video_playlist, list,
        (part) => part === index ? null : (part > index ? part - 1 : part),
      ),
    };
  });
  const moveSegment = (index, direction) => setF((current) => {
    const target = index + direction;
    if (target < 0 || target >= current.video_playlist.length) return current;
    const list = current.video_playlist.map((item) => ({ ...item }));
    [list[index], list[target]] = [list[target], list[index]];
    const chapters = rebaseChapters(current.video_chapters, current.video_playlist, list, (part) => {
      if (part === index) return target;
      if (part === target) return index;
      return part;
    });
    return {
      ...current,
      video_playlist: list.map((item, i) => ({ ...item, part_index: i })),
      video_chapters: chapters,
    };
  });
  const setChapter = (index, key, value) => setF((current) => ({
    ...current,
    video_chapters: current.video_chapters.map((item, i) => i === index ? { ...item, [key]: value } : item),
  }));
  const addChapter = () => setF((current) => ({
    ...current,
    video_chapters: [...current.video_chapters, { title: "فصل جديد", start_seconds: 0, part_index: 0 }],
  }));
  const removeChapter = (index) => setF((current) => ({
    ...current,
    video_chapters: current.video_chapters.filter((_, i) => i !== index),
  }));
  const buildChaptersFromSegments = () => setF((current) => {
    let start = 0;
    const video_chapters = current.video_playlist.map((item, index) => {
      const chapter = {
        title: item.title || `المقطع ${index + 1}`,
        start_seconds: Math.round(start),
        part_index: index,
      };
      start += Number(item.duration_seconds || 0);
      return chapter;
    });
    return { ...current, video_chapters };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/challenges/${ch.id}/video`, {
        ...f,
        video_required_pct: Number(f.video_required_pct),
        video_playlist: f.video_playlist.map((item, index) => ({
          ...item, part_index: index, duration_seconds: Number(item.duration_seconds || 0),
        })),
        video_chapters: f.video_chapters.map((item) => ({
          ...item,
          part_index: Number(item.part_index || 0),
          start_seconds: Number(item.start_seconds || 0),
        })),
      });
      toast.success("تم حفظ إعدادات الفيديو");
      invalidate();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };

  return (
    <Modal title={`فيديو: ${ch.title}`} onClose={onClose} wide testId="video-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="v2-label">رابط فيديو منفرد، أو ارفع من جهازك</label>
          <div className="flex gap-2">
            <input value={f.video_url} onChange={set("video_url")} dir="ltr" data-testid="video-url" className="v2-input" placeholder="https://youtube.com/watch?v=..." />
            <UploadBtn accept="video/*" label="رفع فيديو" testId="video-upload" onDone={(r) => setF((current) => ({ ...current, video_url: r.url }))} />
          </div>
          <p className="text-[11px] text-[#bcc8cf] mt-1.5">يُستخدم هذا الرابط إذا لم تضف قائمة مقاطع أدناه. يدعم YouTube وmp4 وwebm والملفات المرفوعة.</p>
        </div>
        <div><label className="v2-label">عنوان الفيديو</label><input value={f.video_title} onChange={set("video_title")} data-testid="video-title" className="v2-input" /></div>
        <div><label className="v2-label">مدة الفيديو</label><input value={f.video_duration} onChange={set("video_duration")} className="v2-input" /></div>
        <div className="sm:col-span-2"><label className="v2-label">وصف الفيديو</label><textarea rows={2} value={f.video_description} onChange={set("video_description")} className="v2-input resize-none" /></div>

        <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/[0.025] p-4 space-y-3" data-testid="video-playlist-editor">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-bold text-sm flex items-center gap-2"><ListVideo size={16} className="text-[#91cab8]" /> مقاطع المحطة المتتابعة</div>
              <p className="text-[11px] text-[#bcc8cf] mt-1">تعمل بالترتيب داخل مشغّل واحد، وينتقل الموظف تلقائيًا إلى المقطع التالي.</p>
            </div>
            <button type="button" onClick={addSegment} className="v2-btn-ghost v2-btn-sm shrink-0" data-testid="video-segment-add"><Plus size={14} /> إضافة مقطع</button>
          </div>
          {f.video_playlist.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-center text-xs text-[#bcc8cf]">لا توجد قائمة مقاطع؛ سيعمل الرابط المنفرد أعلاه.</div>
          ) : f.video_playlist.map((item, index) => (
            <div key={index} className="rounded-xl bg-[#25343f]/70 border border-white/10 p-3" data-testid={`video-segment-${index}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold text-[#e3c799]">المقطع {index + 1}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveSegment(index, -1)} disabled={index === 0} title="تحريك للأعلى" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center disabled:opacity-25"><ArrowUp size={13} /></button>
                  <button type="button" onClick={() => moveSegment(index, 1)} disabled={index === f.video_playlist.length - 1} title="تحريك للأسفل" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center disabled:opacity-25"><ArrowDown size={13} /></button>
                  <button type="button" onClick={() => removeSegment(index)} title="حذف المقطع" className="w-8 h-8 rounded-lg bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><X size={13} /></button>
                </div>
              </div>
              <div className="grid sm:grid-cols-[1fr_120px] gap-2">
                <input value={item.title || ""} onChange={(e) => setSegment(index, "title", e.target.value)} className="v2-input" placeholder="عنوان المقطع" />
                <input type="number" min="0" step="0.1" value={item.duration_seconds || 0} onChange={(e) => setSegment(index, "duration_seconds", e.target.value)} className="v2-input" placeholder="المدة بالثواني" title="المدة بالثواني" />
                <input value={item.url || ""} onChange={(e) => setSegment(index, "url", e.target.value)} dir="ltr" className="v2-input sm:col-span-2" placeholder="رابط المقطع" />
              </div>
            </div>
          ))}
        </div>

        <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/[0.025] p-4 space-y-3" data-testid="video-chapters-editor">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-bold text-sm">فصول المحطة</div>
              <p className="text-[11px] text-[#bcc8cf] mt-1">وقت البداية محسوب بالثواني من بداية المحطة كاملة.</p>
            </div>
            <div className="flex gap-2">
              {f.video_playlist.length > 0 && <button type="button" onClick={buildChaptersFromSegments} className="v2-btn-ghost v2-btn-sm">مزامنة مع المقاطع</button>}
              <button type="button" onClick={addChapter} className="v2-btn-ghost v2-btn-sm" data-testid="video-chapter-add"><Plus size={14} /> إضافة فصل</button>
            </div>
          </div>
          {f.video_chapters.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-center text-xs text-[#bcc8cf]">لا توجد فصول إضافية.</div>
          ) : f.video_chapters.map((chapter, index) => (
            <div key={index} className="grid sm:grid-cols-[1fr_125px_125px_36px] gap-2 items-center" data-testid={`video-chapter-${index}`}>
              <input value={chapter.title || ""} onChange={(e) => setChapter(index, "title", e.target.value)} className="v2-input" placeholder="عنوان الفصل" />
              <select value={Number(chapter.part_index || 0)} onChange={(e) => setChapter(index, "part_index", Number(e.target.value))} className="v2-input">
                {(f.video_playlist.length ? f.video_playlist : [{ title: "الفيديو", part_index: 0 }]).map((item, part) => <option key={part} value={part}>مقطع {part + 1}</option>)}
              </select>
              <input type="number" min="0" value={Number(chapter.start_seconds || 0)} onChange={(e) => setChapter(index, "start_seconds", Number(e.target.value))} className="v2-input" placeholder="بداية (ث)" />
              <button type="button" onClick={() => removeChapter(index)} title="حذف الفصل" className="w-9 h-9 rounded-lg bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><X size={13} /></button>
            </div>
          ))}
        </div>

        <div>
          <label className="v2-label">الصورة المصغرة</label>
          <div className="flex gap-2">
            <input value={f.thumbnail} onChange={set("thumbnail")} dir="ltr" className="v2-input" />
            <UploadBtn accept="image/*" label="رفع" testId="thumb-upload" onDone={(r) => setF((current) => ({ ...current, thumbnail: r.url }))} />
          </div>
        </div>
        <div><label className="v2-label">نسبة المشاهدة المطلوبة لفتح الاختبار (%)</label><input type="number" min="0" max="100" value={f.video_required_pct} onChange={set("video_required_pct")} data-testid="video-pct" className="v2-input" /></div>
        <div className="sm:col-span-2">
          <label className="v2-label">حالة الفيديو</label>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(VIDEO_STATUS_AR).map(([k, v]) => (
              <button key={k} onClick={() => setF((current) => ({ ...current, video_status: k }))} data-testid={`video-status-${k}`}
                className={`px-4 h-10 rounded-full text-xs font-bold ${f.video_status === k ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>{v}</button>
            ))}
          </div>
          {f.video_status !== "active" && (
            <p className="text-xs mt-2 text-[#e3c799]">عند الإيقاف/القفل: لا يُحذف الفيديو، ولن يستطيع الموظف مشاهدته وستظهر له رسالة «هذا المحتوى متوقف مؤقتًا من الإدارة». يمكن إعادة التشغيل بضغطة واحدة.</p>
          )}
        </div>
        {(f.video_url || f.video_playlist.length > 0) && (
          <div className="sm:col-span-2">
            <button onClick={() => setPreview(!preview)} data-testid="video-preview-btn" className="v2-btn-ghost v2-btn-sm"><Eye size={14} /> {preview ? "إخفاء المعاينة" : "معاينة الفيديو"}</button>
            {preview && <SmartVideo src={f.video_url} playlist={f.video_playlist} chapters={f.video_chapters} title={f.video_title || ch.title} className="mt-3" testId="video-preview" />}
          </div>
        )}
      </div>
      <button onClick={save} disabled={saving} data-testid="video-save" className="v2-btn w-full mt-5">{saving ? "جارٍ الحفظ..." : "حفظ إعدادات الفيديو"}</button>
    </Modal>
  );
}

export default function Videos() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["adm-challenges"] }); qc.invalidateQueries({ queryKey: ["challenges"] }); };

  const quickToggle = async (ch) => {
    const next = (ch.video_status || "active") === "active" ? "disabled" : "active";
    try {
      await api.patch(`/admin/challenges/${ch.id}/video`, { video_status: next });
      toast.success(next === "active" ? "تم إعادة تشغيل الفيديو" : "تم إيقاف الفيديو مؤقتًا");
      invalidate();
    } catch { toast.error("تعذّر التغيير"); }
  };
  const removeVideo = async () => {
    try { await api.delete(`/admin/challenges/${delId}/video`); toast.success("تم حذف الفيديو"); invalidate(); }
    catch { toast.error("تعذّر الحذف"); }
    setDelId(null);
  };

  return (
    <div className="space-y-5" data-testid="admin-videos">
      <PageHead icon={Clapperboard} title="إدارة الفيديوهات" sub="فيديو منفرد أو مقاطع متتابعة بفصول، مع المعاينة ونسبة المشاهدة والإيقاف والتشغيل الفوري." />

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((c) => {
            const st = c.video_status || "active";
            const youtubeId = getYouTubeId(c.video_url || c.video || "");
            const thumbnail = c.thumbnail || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : "/images/paths/default-challenge.svg");
            return (
              <GlassCard key={c.id} hover className="overflow-hidden" data-testid={`video-card-${c.day}`}>
                <div className="relative h-32">
                  <img src={fileUrl(thumbnail)} alt="" className={`w-full h-full object-cover ${st !== "active" ? "grayscale opacity-50" : ""}`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {st === "active" ? <PlayCircle size={38} className="text-white drop-shadow-lg" /> : <PauseCircle size={38} className="text-[#e3c799] drop-shadow-lg" />}
                  </div>
                  <div className="absolute top-2 right-2"><Chip tone="gold">اليوم {c.day}</Chip></div>
                  <div className="absolute top-2 left-2"><Chip tone={V_TONE[st] || "muted"}>{VIDEO_STATUS_AR[st] || st}</Chip></div>
                </div>
                <div className="p-4">
                  <div className="font-bold text-sm truncate mb-1">{c.video_title || c.title}</div>
                  <div className="text-[11px] text-[#bcc8cf] mb-3 truncate" dir="ltr">{c.video_url || c.video || "لا يوجد رابط"}</div>
                  <div className="flex items-center justify-between text-xs text-[#bcc8cf] mb-3">
                    <span>المدة: {c.video_duration}</span>
                    <span>مطلوب: {c.video_required_pct ?? 80}%</span>
                  </div>
                  {(c.video_playlist || []).length > 0 && (
                    <div className="flex gap-2 mb-3">
                      <Chip tone="emerald">{c.video_playlist.length} مقاطع</Chip>
                      <Chip tone="muted">{(c.video_chapters || []).length} فصول</Chip>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(c)} data-testid={`video-edit-${c.day}`} className="v2-btn-ghost v2-btn-sm flex-1"><Pencil size={13} /> إدارة</button>
                    <button onClick={() => quickToggle(c)} data-testid={`video-toggle-${c.day}`}
                      className={st === "active" ? "v2-btn-danger flex-1" : "v2-btn v2-btn-sm flex-1"}>
                      <Power size={13} /> {st === "active" ? "إيقاف" : "تشغيل"}
                    </button>
                    <button onClick={() => setDelId(c.id)} data-testid={`video-del-${c.day}`} className="w-9 h-[38px] rounded-full bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center shrink-0"><Trash2 size={14} /></button>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {editing && <VideoForm ch={editing} onClose={() => setEditing(null)} />}
      {delId && <Confirm title="حذف الفيديو" danger confirmLabel="نعم، احذف"
        body="سيُحذف رابط الفيديو من هذا التحدي (يمكن إضافة فيديو جديد لاحقًا). هل أنت متأكد؟"
        onConfirm={removeVideo} onClose={() => setDelId(null)} testId="video-del-confirm" />}
    </div>
  );
}
